// Persistent per-attachment record store for the re-download pipeline.
//
// IndexedDB-backed, one record per attachmentId. Restart safety is the
// whole point — the scheduler must be able to resume a backoff that started
// before a page reload, otherwise a forced refresh would reset the retry
// budget back to zero and the user's browser would chase the same dead blob
// for hours.
//
// State transitions are delegated to `AttachmentRedownloadStateMachine`;
// this file is mechanical I/O. Once a record is terminal (`expired` or
// `completed`), `applyOutcome` is a no-op, matching the monotonicity
// guarantee on the state machine.
//
// Web mirror of kaether_for_ios/Stoat/E2EE/AttachmentRedownloadStore.swift

import type { AttachmentRedownloadOutcome, AttachmentRedownloadRecord } from "./AttachmentRedownload";
import { startAttempt, transition } from "./AttachmentRedownloadStateMachine";

// ---------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------

const DB_NAME = "kaether_attachment_redownload";
const DB_VERSION = 1;
const STORE_NAME = "records";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "attachmentId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(
  db: IDBDatabase,
  attachmentId: string,
): Promise<AttachmentRedownloadRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(attachmentId);
    req.onsuccess = () => resolve(req.result as AttachmentRedownloadRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(
  db: IDBDatabase,
  record: AttachmentRedownloadRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(db: IDBDatabase, attachmentId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).delete(attachmentId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll(db: IDBDatabase): Promise<AttachmentRedownloadRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result as AttachmentRedownloadRecord[]);
    req.onerror = () => reject(req.error);
  });
}

function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * IndexedDB-backed store for attachment re-download records.
 *
 * All public methods are async and safe to call from multiple async contexts
 * (IndexedDB serialises transactions internally). Consumers should use the
 * module-level `attachmentRedownloadStore` singleton rather than constructing
 * their own instance in production; tests may construct isolated instances via
 * `AttachmentRedownloadStore.forTesting(dbName)`.
 */
export class AttachmentRedownloadStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DB_NAME) {
    this.dbName = dbName;
  }

  /**
   * Create an isolated store instance for use in tests (unique DB name avoids
   * cross-test contamination).
   */
  static forTesting(dbName: string): AttachmentRedownloadStore {
    return new AttachmentRedownloadStore(dbName);
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const req = indexedDB.open(this.dbName, DB_VERSION);
        req.onupgradeneeded = () => {
          const d = req.result;
          if (!d.objectStoreNames.contains(STORE_NAME)) {
            d.createObjectStore(STORE_NAME, { keyPath: "attachmentId" });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    }
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // Registration
  // -------------------------------------------------------------------------

  /**
   * Creates a fresh `pending` record for `attachmentId`. Idempotent: if a
   * record already exists, returns it unchanged (a scroll-into-view event
   * should not reset a `backoff` we are mid-way through).
   */
  async register(
    attachmentId: string,
    at: number = Date.now(),
  ): Promise<AttachmentRedownloadRecord> {
    const db = await this.db();
    const existing = await idbGet(db, attachmentId);
    if (existing) return existing;
    const record: AttachmentRedownloadRecord = {
      attachmentId,
      state: "pending",
      attemptCount: 0,
      lastAttemptedAt: at,
      nextEligibleAt: at,
      failureHistory: [],
    };
    await idbPut(db, record);
    return record;
  }

  // -------------------------------------------------------------------------
  // Driving the state machine
  // -------------------------------------------------------------------------

  /**
   * Marks the record as `inFlight` and stamps `lastAttemptedAt`. Returns
   * `undefined` if the record does not exist. Terminal records are returned
   * unchanged (sticky verdicts).
   */
  async markInFlight(
    attachmentId: string,
    at: number = Date.now(),
  ): Promise<AttachmentRedownloadRecord | undefined> {
    const db = await this.db();
    const existing = await idbGet(db, attachmentId);
    if (!existing) return undefined;
    const next = startAttempt(existing, at);
    await idbPut(db, next);
    return next;
  }

  /**
   * Applies an outcome through the state machine and persists the result.
   * Returns the updated record, or `undefined` if not registered. Terminal
   * records short-circuit — once `expired`, a later 404 (or any other
   * outcome) is a no-op.
   */
  async applyOutcome(
    attachmentId: string,
    outcome: AttachmentRedownloadOutcome,
    at: number = Date.now(),
  ): Promise<AttachmentRedownloadRecord | undefined> {
    const db = await this.db();
    const existing = await idbGet(db, attachmentId);
    if (!existing) return undefined;
    const next = transition(existing, outcome, at);
    await idbPut(db, next);
    return next;
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  async record(
    attachmentId: string,
  ): Promise<AttachmentRedownloadRecord | undefined> {
    const db = await this.db();
    return idbGet(db, attachmentId);
  }

  /**
   * Records in `backoff` whose `nextEligibleAt` has passed, sorted
   * ascending by `nextEligibleAt` — the scheduler iterates this list and
   * kicks off the most-overdue downloads first.
   */
  async dueForRetry(at: number = Date.now()): Promise<AttachmentRedownloadRecord[]> {
    const db = await this.db();
    const all = await idbGetAll(db);
    return all
      .filter((r) => r.state === "backoff" && r.nextEligibleAt <= at)
      .sort((a, b) => a.nextEligibleAt - b.nextEligibleAt);
  }

  async expiredAttachmentIds(): Promise<Set<string>> {
    const db = await this.db();
    const all = await idbGetAll(db);
    return new Set(all.filter((r) => r.state === "expired").map((r) => r.attachmentId));
  }

  /**
   * Current count of `inFlight` records — feeds `canStartNew` to enforce the
   * bandwidth cap.
   */
  async inFlightCount(): Promise<number> {
    const db = await this.db();
    const all = await idbGetAll(db);
    return all.filter((r) => r.state === "inFlight").length;
  }

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  async forget(attachmentId: string): Promise<void> {
    const db = await this.db();
    await idbDelete(db, attachmentId);
  }

  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db);
  }
}

/** Module-level singleton for production use. */
export const attachmentRedownloadStore = new AttachmentRedownloadStore();
